import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import { formatAgeRange, formatGeographicRestriction } from '../../../hooks/utils/formatProgramInfo';
import ProgramInfoItem from './ProgramInfoItem';
import programPropsShape, { defaultProgramProps } from '../programPropsShape';
import messages, {
  accOptionsMessages,
  deliveryOptMessages,
  languageOptMessages,
  populationOptMessages,
  paymentOptMessages,
  transportationOptMessages,
  translateOptions,
} from './messages';
import useFilteredPopulations from '../../../hooks/useFilteredPopulations';

export default function ProgramInfo({
  accessibility_options,
  age_requirements,
  age_specializations,
  city_requirements,
  county_requirements,
  delivery_options,
  displayName,
  gender_requirements,
  languages,
  state_requirements,
  population_restrictions,
  population_specializations,
  postal_code_requirements,
  payment_options,
  transportation_options,
}) {
  const { formatList, formatMessage } = useIntl();

  const filteredPopulationSpecializations = useFilteredPopulations(population_specializations);
  const filteredPopulationRestrictions = useFilteredPopulations(population_restrictions);

  const formattedProps = useMemo(() => {
    const agesSpecializedText = formatAgeRange(age_specializations);
    const agesServedText = formatAgeRange(age_requirements);
    const translate = translateOptions(formatMessage);

    const accOptions = translate(accessibility_options, accOptionsMessages);
    const deliveryOptions = translate(delivery_options, deliveryOptMessages);
    const languageOptions = translate(languages, languageOptMessages);
    const populationSpecializationsOptions = translate(filteredPopulationSpecializations, populationOptMessages);
    const populationRestrictionsOptions = translate(filteredPopulationRestrictions, populationOptMessages);
    const paymentOptions = translate(payment_options, paymentOptMessages);
    const transportationOptions = translate(transportation_options, transportationOptMessages);

    return {
      agesServedText,
      agesSpecializedText,
      accessibilityServices: formatList(accOptions, { type: 'unit' }),
      cityRequirementsText: formatGeographicRestriction(city_requirements),
      countyRequirementsText: formatGeographicRestriction(county_requirements),
      deliveryOptionsText: formatList(deliveryOptions, { type: 'unit' }),
      genderRequirementsText: gender_requirements.join(', '),
      languagesSpokenText: formatList(languageOptions, { type: 'unit' }),
      populationRestrictionsText: formatList(populationRestrictionsOptions, { type: 'unit' }),
      populationSpecializationsText: formatList(populationSpecializationsOptions, { type: 'unit' }),
      postalCodeRequirementsText: postal_code_requirements.join(', '),
      stateRequirementsText: state_requirements.join(', '),
      paymentOptionsText: formatList(paymentOptions, { type: 'unit' }),
      transportationOptionsText: formatList(transportationOptions, { type: 'unit' }),

    };
  }, [
    accessibility_options,
    age_requirements,
    age_specializations,
    city_requirements,
    county_requirements,
    delivery_options,
    filteredPopulationSpecializations,
    gender_requirements,
    languages,
    state_requirements,
    filteredPopulationRestrictions,
    postal_code_requirements,
    payment_options,
    transportation_options,
    formatList,
    formatMessage,
  ]);

  const {
    agesServedText,
    agesSpecializedText,
    accessibilityServices,
    cityRequirementsText,
    countyRequirementsText,
    deliveryOptionsText,
    genderRequirementsText,
    populationRestrictionsText,
    languagesSpokenText,
    populationSpecializationsText,
    postalCodeRequirementsText,
    stateRequirementsText,
    paymentOptionsText,
    transportationOptionsText,
  } = formattedProps;

  const hasWhoWeServeValues = !isEmpty(stateRequirementsText)
    || !isEmpty(postalCodeRequirementsText)
    || !isEmpty(countyRequirementsText)
    || !isEmpty(cityRequirementsText)
    || !isEmpty(agesServedText)
    || !isEmpty(genderRequirementsText)
    || !isEmpty(populationRestrictionsText);

  const hasOtherInfoValues = !isEmpty(deliveryOptionsText)
    || !isEmpty(languagesSpokenText)
    || !isEmpty(populationSpecializationsText)
    || !isEmpty(agesSpecializedText)
    || !isEmpty(accessibilityServices)
    || !isEmpty(paymentOptionsText)
    || !isEmpty(transportationOptionsText);

  return (
    <>
      <div className={`${displayName}--who-we-serve`}>
        <h3>
          <strong>
            <FormattedMessage
              id="ProgrameInfo.whoWeServe"
              defaultMessage="Who We Serve:"
            />
          </strong>
        </h3>
        {hasWhoWeServeValues ? (
          <ul>
            {!isEmpty(stateRequirementsText)
              && <ProgramInfoItem title={formatMessage(messages.states)} options={stateRequirementsText} />}
            {postalCodeRequirementsText
              && <ProgramInfoItem title={formatMessage(messages.zipCodes)} options={postalCodeRequirementsText} />}
            {!isEmpty(countyRequirementsText)
              && <ProgramInfoItem title={formatMessage(messages.counties)} options={countyRequirementsText} />}
            {!isEmpty(cityRequirementsText)
              && <ProgramInfoItem title={formatMessage(messages.cities)} options={cityRequirementsText} />}
            {!isEmpty(agesServedText)
              && <ProgramInfoItem title={formatMessage(messages.ages)} options={agesServedText} />}
            {!isEmpty(genderRequirementsText)
              && <ProgramInfoItem title={formatMessage(messages.genders)} options={genderRequirementsText} />}
            {!isEmpty(populationRestrictionsText)
                && <ProgramInfoItem title={formatMessage(messages.populations)} options={populationRestrictionsText} />}
          </ul>
        ) : (
          <span className="italic">
            <FormattedMessage
              id="ProgramInfo.noPopulationSpecialization"
              defaultMessage="This program does not have any population specialization information."
            />
          </span>
        )}
      </div>
      <div className={`${displayName}--other-info`}>
        <h3>
          <strong>
            <FormattedMessage
              id="ProgramSearch.otherInformation"
              defaultMessage="Other Important Information:"
            />
          </strong>
        </h3>
        {hasOtherInfoValues ? (
          <ul>
            {!isEmpty(deliveryOptionsText)
              && <ProgramInfoItem title={formatMessage(messages.programDelivery)} options={deliveryOptionsText} />}
            {!isEmpty(languagesSpokenText)
              && <ProgramInfoItem title={formatMessage(messages.supportedLanguages)} options={languagesSpokenText} />}
            {!isEmpty(populationSpecializationsText)
              && <ProgramInfoItem title={formatMessage(messages.populationsSpecialized)} options={populationSpecializationsText} />}
            {!isEmpty(agesSpecializedText)
              && <ProgramInfoItem title={formatMessage(messages.agesSpecializedIn)} options={agesSpecializedText} />}
            {!isEmpty(accessibilityServices)
              && <ProgramInfoItem title={formatMessage(messages.accessibilityServices)} options={accessibilityServices} />}
            {!isEmpty(transportationOptionsText)
              && <ProgramInfoItem title={formatMessage(messages.transportationOptions)} options={transportationOptionsText} />}
            {!isEmpty(paymentOptionsText)
              && <ProgramInfoItem title={formatMessage(messages.paymentOptions)} options={paymentOptionsText} />}
          </ul>
        ) : (
          <span className="italic">
            <FormattedMessage
              id="ProgramInfo.noAdditionalInformation"
              defaultMessage="This program does not have any additional information."
            />
          </span>
        )}
      </div>
    </>
  );
}

ProgramInfo.propTypes = {
  ...programPropsShape,
  displayName: PropTypes.string.isRequired,
};

ProgramInfo.defaultProps = {
  ...defaultProgramProps,
};
